import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  tap,
} from "rxjs/operators";
import { Epic, ofType } from "redux-observable";
import { concat, from, of } from "rxjs";
import { RootState } from "../../store";
import * as organizationActions from "./organizationActions";
import * as authSelectors from "../auth/authSelectors";
import { ServiceContainer } from "../../serviceContainer";
import { AjaxError } from "rxjs/ajax";
import { AnyAction } from "redux";

export const updateUISettings: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(organizationActions.UPSERT_UI_SETTINGS),
    mergeMap((action: organizationActions.UpsertUISettingsAction) => {
      const state = state$.value;
      return services.apiOrganization
        .setUISettings(
          authSelectors.accessToken(state),
          action.organization,
          action.data
        )
        .pipe(
          tap(() =>
            services.toastr.success(
              "Success",
              "Organization UI Settings were updated"
            )
          ),
          mergeMap(() =>
            concat(
              of(
                organizationActions.upsertUISettingsSuccess(action.organization)
              ),
              services.apiOrganization
                .getUISettings(
                  authSelectors.accessToken(state),
                  action.organization
                )
                .pipe(
                  map((uiSettings) =>
                    organizationActions.getUISettingsSuccess(
                      action.organization,
                      uiSettings
                    )
                  )
                )
            )
          ),
          catchError((x: AjaxError) => {
            if (
              x.status === 400 &&
              x.xhr.response.errors &&
              x.xhr.response.errors.length
            ) {
              services.toastr.warning(
                "Warning",
                `Some values in the form are invalid`
              );
              return of(
                organizationActions.upsertUISettingsFail(
                  action.organization,
                  x.xhr.response.errors
                )
              );
            } else {
              services.toastr.error(
                "Error",
                `There was problem with updating the settings`
              );

              return of(
                organizationActions.upsertUISettingsFail(
                  action.organization,
                  x.xhr.response.errors
                )
              );
            }
          })
        );
    })
  );

export const organizationListEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(organizationActions.LIST_ORGANIZATIONS),
    exhaustMap((action: organizationActions.ListOrganizationsAction) => {
      const state = state$.value;

      return services.apiOrganization
        .list(authSelectors.accessToken(state), action.organization)
        .pipe(
          map((response) =>
            organizationActions.listOrganizationsSuccessAction(
              action.organization,
              response.organizations
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching organization list"
            );
            return of(
              organizationActions.listOrganizationsFailAction(
                action.organization
              )
            );
          })
        );
    })
  );

export const getOrganizationEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(organizationActions.GET_ORGANIZATION),
    exhaustMap((action: organizationActions.GetOrganizationAction) => {
      const state = state$.value;

      return services.apiOrganization
        .get(authSelectors.accessToken(state), action.organization)
        .pipe(
          map((response) =>
            organizationActions.getOrganizationSuccessAction(
              action.organization,
              response.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching organization"
            );
            return of(
              organizationActions.getOrganizationFailAction(action.organization)
            );
          })
        );
    })
  );

export const upsertOrganizationEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    filter((action) => action.type === organizationActions.UPSERT_ORGANIZATION),
    mergeMap((action: organizationActions.UpsertOrganizationAction) => {
      const promise = async (): Promise<AnyAction[]> => {
        try {
          let r = [];
          const token = authSelectors.accessToken(state$.value);
          let insertedOrganization;

          if (!!action.organizationKeyName) {
            await services.apiOrganization
              .update(token, action.organizationKeyName, action.data)
              .toPromise();

            services.toastr.success("Success", "Organization updated");
          } else {
            insertedOrganization = await services.apiOrganization
              .insert(token, action.data)
              .toPromise();

            services.toastr.success("Success", "Organization added");
          }

          r.push(
            organizationActions.upsertOrganizationSuccessAction(
              action.organizationKeyName || insertedOrganization.data.keyName
            )
          );

          return r;
        } catch (e: any) {
          return [
            organizationActions.upsertOrganizationFailAction(
              e.response.errors,
              action.organizationKeyName
            ),
          ];
        }
      };

      return from(promise()).pipe(mergeMap(from));
    })
  );
